'use client'

import { useEffect, useRef, useCallback } from 'react'
import { useSession } from 'next-auth/react'
import { trpc } from '@/lib/trpc'

/** How often to send heartbeats (ms) */
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Hook that keeps the user's platform session alive and handles cleanup.
 *
 * - On mount: ensures a UserSession row exists (covers returning users with valid JWTs
 *   but no active session — e.g., after timeout, beacon, or first load post-deployment)
 * - Sends a heartbeat every 5 minutes (the tRPC middleware does the actual touch)
 * - Pauses heartbeat when the tab is hidden (visibilitychange)
 * - Sends a beacon on page close to end the session
 *
 * Mount once in the root providers — only runs when the user is authenticated.
 */
export function useSessionTracking() {
  const { data: session, status } = useSession()
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasInitialized = useRef(false)

  // Store mutations in refs so they don't cause effect re-runs.
  // useMutation() returns a new object on every render; using refs
  // keeps the effect dependency array stable.
  const heartbeat = trpc.session.heartbeat.useMutation()
  const ensureSession = trpc.session.ensureSession.useMutation()
  const heartbeatRef = useRef(heartbeat)
  const ensureSessionRef = useRef(ensureSession)
  useEffect(() => { heartbeatRef.current = heartbeat }, [heartbeat])
  useEffect(() => { ensureSessionRef.current = ensureSession }, [ensureSession])

  const startHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) return
    heartbeatTimer.current = setInterval(() => {
      heartbeatRef.current.mutate(undefined, {
        // Silently ignore errors — heartbeat is best-effort
        onError: () => {},
      })
    }, HEARTBEAT_INTERVAL_MS)
  }, [])

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current)
      heartbeatTimer.current = null
    }
  }, [])

  useEffect(() => {
    if (status !== 'authenticated' || !session?.user) return
    if (hasInitialized.current) return
    hasInitialized.current = true

    // Ensure a platform session exists before starting the heartbeat.
    // This covers returning users whose JWT is still valid but whose
    // UserSession was ended (timeout, beacon) or never created.
    ensureSessionRef.current.mutate(undefined, {
      onSuccess: () => {
        // Session is guaranteed to exist — start heartbeat
        startHeartbeat()
      },
      onError: () => {
        // Best effort — start heartbeat anyway so middleware touches work
        // if a session exists or gets created later
        startHeartbeat()
      },
    })

    // Pause/resume on visibility change
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopHeartbeat()
      } else {
        startHeartbeat()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    // End session on page close via beacon
    const handlePageHide = () => {
      navigator.sendBeacon('/api/session/end')
    }
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      stopHeartbeat()
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handlePageHide)
      hasInitialized.current = false
    }
  }, [status, session?.user, startHeartbeat, stopHeartbeat])
}
